import React, { ComponentProps, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box, Sidecar, Typography, Button, InlineLink } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { classNames } from '@fullstory/util/fmt';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { useFlags } from '@fullstory/react-hooks';
import { ContactUs, ContactHeader, ReasonId } from '../ContactUs';
import { Footer } from '../ContactFooter';
import { Carousel } from '../Carousel';
import css from './index.module.scss';

enum ContactPaneId {
  ContactUs = 'contactUs',
  Thanks = 'thanks',
  Features = 'features',
}

interface ContactSidecarProps {
  closeFn: () => void;
  initialPane?: ContactPaneId; // Defaults to ContactUs
  initialReasonId?: ReasonId; // Defaults to AccountChange
  position?: ComponentProps<typeof Sidecar>['position'];
  isUpgradePage?: boolean;
  open?: boolean;
}

const PlanContactSidecar: React.FunctionComponent<ContactSidecarProps> = ({
  closeFn,
  initialPane = ContactPaneId.ContactUs,
  initialReasonId = ReasonId.AccountChange,
  position = 'right',
  isUpgradePage = false,
  open = true,
}) => {
  const [pane, setPane] = useState(initialPane);

  const goToContactUs = () => setPane(ContactPaneId.ContactUs);
  const goToThanks = () => setPane(ContactPaneId.Thanks);

  let content = null;
  switch (pane) {
    default:
    case ContactPaneId.ContactUs:
      content = (
        <Box paddingTop={32} paddingLeft={48} paddingRight={48}>
          <Box marginTop={24} marginBottom={64}>
            <ContactHeader isUpgradePage={isUpgradePage} />
          </Box>
          <ContactUs
            initialReasonId={initialReasonId}
            closeFn={closeFn}
            onSend={goToThanks}
            isUpgradePage={isUpgradePage}
          />
        </Box>
      );
      break;
    case ContactPaneId.Thanks:
      content = (
        <Thanks
          closeFn={closeFn}
          buttonText={intl.formatMessage({ defaultMessage: 'Return to app', id: 'SDua+Y' })}
        />
      );
      break;
    case ContactPaneId.Features:
      content = <Features next={goToContactUs} />;
  }

  return (
    <Sidecar
      data-component="PlanContactSidecar"
      open={open}
      testId="contact-us-sidecar"
      position={position}
      onClose={closeFn}
    >
      {content}
    </Sidecar>
  );
};

interface ThanksProps {
  closeFn: () => void;
  buttonText: string;
  className?: string;
}

function Thanks(props: ThanksProps) {
  return (
    <Box
      data-component="Thanks"
      className={props.className}
      paddingTop={48}
      paddingLeft={48}
      paddingRight={48}
    >
      <Box marginBottom={32}>
        <Typography color="neutrals-n11" variant="title1">
          <FormattedMessage defaultMessage="Thanks! We'll be in touch." id="aLxOtm" />
        </Typography>
      </Box>
      <Box marginBottom={32}>
        <Typography variant="body1">
          <FormattedMessage
            defaultMessage="Got it! We will be in touch with you via email within 1 business day regarding your request."
            id="F0mISU"
          />
        </Typography>
      </Box>
      <Footer>
        <Box marginBottom={16}>
          <Button
            variant="fill-primary"
            className={css.wideButton}
            size="large"
            onClick={props.closeFn}
          >
            {props.buttonText}
          </Button>
        </Box>
      </Footer>
    </Box>
  );
}

interface FeaturesProps {
  next: () => void;
}

function Features(props: FeaturesProps) {
  const flags = useFlags();
  const hasDevTools = flags?.hasBit(Flag.FeatDevtools) ?? false;

  const onClick = (e: React.MouseEvent) => {
    e.nativeEvent.stopImmediatePropagation();
    props.next();
  };

  return (
    <Box data-component="Features" className={css.features} paddingTop={64} paddingBottom={32}>
      <Box paddingTop={32} paddingLeft={48} paddingRight={48}>
        <Typography color="neutrals-n11" variant="title1">
          <FormattedMessage defaultMessage="Unlock the magic of Fullstory." id="iBIN+3" />
        </Typography>
      </Box>
      <Carousel>
        {!hasDevTools && <FeatureCard feature="devtools" />}
        <FeatureCard feature="journeys" />
        <FeatureCard feature="sso" />
        <FeatureCard feature="alerts" />
        <FeatureCard feature="conversions" />
        <FeatureCard feature="data" />
        <FeatureCard feature="story" />
      </Carousel>
      <Footer>
        <Box marginBottom={16} paddingLeft={48} paddingRight={48}>
          <Button
            variant="fill-primary"
            className={css.wideButton}
            size="large"
            onClick={onClick}
            iconRight="arrow-right"
            justifyContent="center"
          >
            <FormattedMessage defaultMessage="Talk to us" id="aX9C8R" />
          </Button>
        </Box>
      </Footer>
    </Box>
  );
}

interface FeatureCardProps {
  feature: 'journeys' | 'sso' | 'alerts' | 'conversions' | 'data' | 'story' | 'devtools';
  isChecked?: boolean;
}

interface FeatureCardContentType {
  headerText: string;
  bodyText: string;
  href: string;
  description: string;
}

const featureCardContent: { [key in FeatureCardProps['feature']]: FeatureCardContentType } = {
  journeys: {
    headerText: intl.formatMessage({
      defaultMessage: 'Understand your users’ digital journeys',
      id: '7kb4ki',
    }),
    bodyText: intl.formatMessage({
      defaultMessage: 'Identify and analyze common paths your users take with Journeys.',
      id: 'Vyyr/0',
    }),
    href: 'https://help.fullstory.com/hc/en-us/articles/4410019422743',
    description: intl.formatMessage({
      defaultMessage: 'Available in enterprise & advanced',
      id: 'k3Cmqk',
    }),
  },
  sso: {
    headerText: intl.formatMessage({
      defaultMessage: 'Single Sign-On (SSO)',
      id: 'dXpfD3',
    }),
    bodyText: intl.formatMessage({
      defaultMessage:
        'Manage user access with your preferred SSO provider and add new users automatically with JIT provisioning.',
      id: 'FEaZje',
    }),
    href: '',
    description: intl.formatMessage({
      defaultMessage: 'Available in enterprise',
      id: 'FSzfxM',
    }),
  },
  alerts: {
    headerText: intl.formatMessage({ defaultMessage: 'Alerts', id: '9zub+u' }),
    bodyText: intl.formatMessage({
      defaultMessage:
        'Know when your segments are succeeding or struggling with the ability to create custom alerts.',
      id: 'a2/dId',
    }),
    href: 'https://help.fullstory.com/introduction-to-alerts',
    description: intl.formatMessage({
      defaultMessage: 'Available in enterprise & advanced',
      id: 'k3Cmqk',
    }),
  },
  conversions: {
    headerText: intl.formatMessage({ defaultMessage: 'Conversions', id: 'WuHoZ9' }),
    bodyText: intl.formatMessage({
      defaultMessage:
        'Illuminate problems in your digital experience and shine a light on revenue-impacting opportunities.',
      id: 'a9+jBT',
    }),
    href: 'https://www.fullstory.com/conversions',
    description: intl.formatMessage({
      defaultMessage: 'Available in enterprise & advanced',
      id: 'k3Cmqk',
    }),
  },
  data: {
    headerText: intl.formatMessage({ defaultMessage: 'Data Export', id: 'zWElXZ' }),
    bodyText: intl.formatMessage({
      defaultMessage:
        'Receive periodic, raw extract of events recorded for your organization and an API endpoint to retrieve it.',
      id: 'MHadyg',
    }),
    href: 'https://www.fullstory.com/resources/guide-to-data-export',
    description: intl.formatMessage({
      defaultMessage: 'Available in enterprise & advanced',
      id: 'k3Cmqk',
    }),
  },
  devtools: {
    headerText: intl.formatMessage({ defaultMessage: 'Dev Tools', id: 'COmZec' }),
    bodyText: intl.formatMessage({
      defaultMessage:
        'Analyze mysterious bugs as they occur in the wild and understand what impacts loading speeds.',
      id: 'Y43kBY',
    }),
    href: 'https://www.fullstory.com/features/dev-tools/',
    description: intl.formatMessage({
      defaultMessage: 'Available with upgrade',
      id: 'mA//8P',
    }),
  },
  story: {
    headerText: intl.formatMessage({
      defaultMessage: "The story doesn't end here...",
      id: 'rD+XWW',
    }),
    bodyText: intl.formatMessage({
      defaultMessage:
        'Learn more about these features and what you gain when you upgrade to enterprise.',
      id: 'FJ0gHc',
    }),
    href: '',
    description: '',
  },
};

function FeatureCard(props: FeatureCardProps) {
  const content = featureCardContent[props.feature];
  return (
    <>
      <label className={classNames(css.featureCard, css[props.feature])} htmlFor={props.feature}>
        <div className={css.ad} />
        <Box
          style={{
            textAlign: props.feature == 'story' ? 'center' : 'left',
          }}
          marginBottom={16}
        >
          <Typography color="neutrals-n11" variant="heading3">
            {content.headerText}
          </Typography>
        </Box>
        <Box
          style={{
            textAlign: props.feature == 'story' ? 'center' : 'left',
            lineHeight: '1.5',
          }}
          marginBottom={16}
        >
          <Typography color="neutrals-n8" variant="body2">
            {content.bodyText}
          </Typography>
          {content.href && (
            <Box inline>
              &nbsp;
              {/* These are technically links to external domains, but they are all owned by us */}
              {/* eslint-disable-next-line react/jsx-no-target-blank */}
              <InlineLink href={content.href} target="_blank">
                <Typography color="inherit" variant="body2">
                  <FormattedMessage defaultMessage="Learn more" id="TdTXXf" />
                </Typography>
              </InlineLink>
            </Box>
          )}
        </Box>
        <Box>
          <Typography variant="heading7" color="neutrals-n8">
            {content.description}
          </Typography>
        </Box>
      </label>
      <input
        id={props.feature}
        name="features"
        type="radio"
        checked={props.isChecked == true ? props.isChecked : false}
        readOnly
      />
    </>
  );
}

// Convenience function to open a PlanContactSidecar from tomato code
export { PlanContactSidecar, ContactPaneId, Footer, Thanks };
